/**
 * WelcomeOverlay — splash/launcher screen for the editor.
 *
 * Shows every time the editor loads with an empty active tab (no pipeline
 * steps, no input). Provides recent tabs, quick actions, and auto-dismisses
 * on any interaction (typing, pasting, or selecting an action).
 */

import { FileUp, Layers, Plus, Search } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ToolRegistry } from '@/engine/ToolRegistry';
import { createFileInput, importInputFile } from '@/lib/fileUtils';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useTabStore } from '@/store/useTabStore';

// ─── Prism logo (inline SVG) ───────────────────────────────────────────────

const PrismLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 64 64"
    fill="none"
    className={className}
  >
    <polygon
      points="32,10 54,50 10,50"
      fill="none"
      stroke="url(#welcome-prism-grad)"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <line x1="8" y1="30" x2="26" y2="33" stroke="#e2e8f0" strokeWidth="1.5" opacity="0.7" />
    <line x1="38" y1="28" x2="56" y2="18" stroke="#f87171" strokeWidth="1.5" opacity="0.8" />
    <line x1="38" y1="31" x2="56" y2="28" stroke="#fbbf24" strokeWidth="1.5" opacity="0.8" />
    <line x1="38" y1="34" x2="56" y2="38" stroke="#34d399" strokeWidth="1.5" opacity="0.8" />
    <line x1="38" y1="37" x2="56" y2="48" stroke="#60a5fa" strokeWidth="1.5" opacity="0.8" />
    <defs>
      <linearGradient id="welcome-prism-grad" x1="10" y1="50" x2="54" y2="10">
        <stop offset="0%" stopColor="#818cf8" />
        <stop offset="100%" stopColor="#c084fc" />
      </linearGradient>
    </defs>
  </svg>
);

// ─── Helpers ────────────────────────────────────────────────────────────────

const FILE_IMPORT_ACCEPT =
  '.json,.csv,.tsv,.xml,.yaml,.yml,.html,.htm,.svg,.md,.markdown,.txt,.toml,.sql,.css,.js,.ts,.jsx,.tsx,text/*,application/json,application/xml,application/x-yaml';

/** Resolve a pipeline's tool names into a human-readable summary. */
function summarizePipeline(toolIds: string[]): string {
  if (toolIds.length === 0) return 'Empty pipeline';
  const names = toolIds
    .slice(0, 3)
    .map((id) => ToolRegistry.get(id)?.name ?? id);
  if (toolIds.length > 3) {
    return `${names.join(' → ')} +${toolIds.length - 3} more`;
  }
  return names.join(' → ');
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface WelcomeOverlayProps {
  /** Called when the overlay wants to dismiss itself. */
  onDismiss: () => void;
}

export const WelcomeOverlay: React.FC<WelcomeOverlayProps> = ({ onDismiss }) => {
  const { t } = useTranslation(['editor', 'common']);
  const { setInput } = useAppStore();
  const tabs = useTabStore((s) => s.tabs);
  const switchTab = useTabStore((s) => s.switchTab);
  const createTab = useTabStore((s) => s.createTab);
  const activeTabId = useTabStore((s) => s.activeTabId);

  // Fade-in animation state
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Recent tabs: other tabs with pipeline steps (non-empty, not the current empty one)
  const recentTabs = useMemo(() => {
    return tabs.filter(
      (t) => t.id !== activeTabId && t.pipeline.length > 0,
    );
  }, [tabs, activeTabId]);

  // Dismiss with fade-out
  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(onDismiss, 150);
  }, [onDismiss]);

  // Auto-dismiss when the user types or pastes into the input
  useEffect(() => {
    const unsub = useEditorStore.subscribe((state, prevState) => {
      if (state.input !== prevState.input && state.input.length > 0) {
        dismiss();
      }
    });
    return unsub;
  }, [dismiss]);

  // Escape to dismiss
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [dismiss]);

  // ── Actions ─────────────────────────────────────────────────────────────

  const handleSwitchTab = useCallback(
    (tabId: string) => {
      switchTab(tabId);
      dismiss();
    },
    [switchTab, dismiss],
  );

  const handleNewPipeline = useCallback(() => {
    createTab('New Pipeline');
    dismiss();
  }, [createTab, dismiss]);

  const handleImportFile = useCallback(() => {
    createFileInput(FILE_IMPORT_ACCEPT, async (file) => {
      const result = await importInputFile(file);
      setInput(result.content);
    });
    dismiss();
  }, [setInput, dismiss]);

  const handleBrowseTools = useCallback(() => {
    useLayoutStore.getState().openDrawer('tools');
    dismiss();
  }, [dismiss]);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div
      className={`absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-150 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        className={`w-full max-w-lg rounded-2xl border border-border bg-background/95 shadow-2xl p-8 transition-all duration-200 ${
          visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Branding */}
        <div className="flex flex-col items-center gap-3 mb-8">
          <PrismLogo className="h-12 w-12" />
          <h1 className="text-xl font-semibold text-foreground tracking-tight">
            {t('editor:welcome.title')}
          </h1>
          <p className="text-xs text-muted-foreground text-center max-w-xs">
            {t('editor:welcome.subtitle')}
          </p>
        </div>

        {/* Recent tabs */}
        {recentTabs.length > 0 && (
          <div className="mb-6">
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
              {t('editor:welcome.pickUpWhereLeftOff')}
            </h2>
            <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
              {recentTabs.slice(0, 5).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleSwitchTab(tab.id)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-muted/30 hover:bg-muted/60 hover:border-violet-500/30 transition-all duration-150 text-left group"
                >
                  <Layers className="h-4 w-4 text-muted-foreground group-hover:text-violet-400 shrink-0 transition-colors" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">
                      {tab.name}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {summarizePipeline(tab.pipeline.map((s) => s.toolId))}
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground/50 shrink-0">
                    {t('editor:welcome.stepCount', { count: tab.pipeline.length })}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div>
          <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
            {t('editor:welcome.startFresh')}
          </h2>
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-1.5 text-xs hover:border-violet-500/40 hover:bg-violet-500/[0.04]"
              onClick={handleNewPipeline}
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
              <span>{t('editor:welcome.newPipeline')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-1.5 text-xs hover:border-violet-500/40 hover:bg-violet-500/[0.04]"
              onClick={handleImportFile}
            >
              <FileUp className="h-4 w-4 text-muted-foreground" />
              <span>{t('editor:welcome.importFile')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-1.5 text-xs hover:border-violet-500/40 hover:bg-violet-500/[0.04]"
              onClick={handleBrowseTools}
            >
              <Search className="h-4 w-4 text-muted-foreground" />
              <span>{t('editor:welcome.browseTools')}</span>
            </Button>
          </div>
        </div>

        {/* Keyboard hint */}
        <p className="text-[10px] text-muted-foreground/40 text-center mt-5">
          Press <kbd className="px-1 py-0.5 rounded border border-border bg-muted/50 text-[9px] font-mono">Esc</kbd> {t('editor:welcome.escToDismiss')}
          {' · '}
          <kbd className="px-1 py-0.5 rounded border border-border bg-muted/50 text-[9px] font-mono">Ctrl+K</kbd> {t('editor:welcome.ctrlKToSearch')}
        </p>
      </div>
    </div>
  );
};
